import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router'
import { api, ApiError, type Portfolio } from '../api.js'
import { atLeast, useAuth } from '../auth.js'
import { usePref } from '../prefs.js'
import './portfolio.css'

const roleLabel = (r: Portfolio['myRole']) => r === 'admin' ? tr("portfolio administrator") : r ?? '—'

export function PortfoliosPage() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const nav = useNavigate()
  const [showArchived, setShowArchived] = usePref<boolean>('portfoliosShowArchived', false)
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios') })
  const canCreate = atLeast(user, 'manager')
  const [name, setName] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => api<{ portfolio: Portfolio }>('/portfolios', { method: 'POST', json: { name } }),
    onSuccess: (r) => { setName(''); void qc.invalidateQueries({ queryKey: ['portfolios'] }); nav(`/portfolios/${r.portfolio.id}`) },
    onError: (e) => setErr(e instanceof ApiError ? e.message : tr("Could not create portfolio")),
  })
  const rows = (portfolios.data?.portfolios ?? []).filter((p) => showArchived || !p.archivedAt).sort((a, b) => a.name.localeCompare(b.name))
  const archivedCount = (portfolios.data?.portfolios ?? []).filter((p) => p.archivedAt).length

  return (
    <>
      <div className="row between">
        <div><h1>{tr("Portfolios")}</h1><p className="muted">{tr("A portfolio groups related projects under one initiative (a client, a programme, a department) with a shared board, list and Gantt.")}</p></div>
      </div>
      <div className="row toolbar">
        <label className="inline"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> {tr("Show archived")}{archivedCount ? ` (${archivedCount})` : ''}</label>
      </div>
      <section className="card">
        {portfolios.isPending && <p className="muted">{tr("Loading…")}</p>}
        {portfolios.data && rows.length === 0 && <p className="muted">{tr("No portfolios yet.")}</p>}
        {rows.length > 0 && (
          <table className="table pftable">
            <thead><tr><th>{tr("Name")}</th><th>{tr("Description")}</th><th className="num">{tr("Projects")}</th><th>{tr("Portfolio manager")}</th><th>{tr("Your role")}</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className={p.archivedAt ? 'muted' : ''}>
                  <td><Link to={`/portfolios/${p.id}`} data-tip={tr("Open the portfolio")}><strong>{p.name}</strong></Link>{p.archivedAt && <span className="tag">{tr("archived")}</span>}</td>
                  <td className="small muted">{p.description ?? ''}</td>
                  <td className="num mono">{p.projectCount}</td>
                  <td>{p.ownerName ?? <span className="muted">—</span>}</td>
                  <td>{p.myRole ? roleLabel(p.myRole) : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {canCreate && (
        <section className="card stack">
          <h2>{tr("New portfolio")}</h2>
          <ul className="help small muted">
            <li>{tr("You become its portfolio manager; add projects and members from the portfolio page, or file a project from its")} <strong>{tr("Settings")}</strong>.</li>
            <li>{tr("Portfolio roles grant access to the merged views; task edits still follow each project's own membership.")}</li>
          </ul>
          <form className="row" onSubmit={(e) => { e.preventDefault(); setErr(null); create.mutate() }}>
            <input placeholder={tr("New portfolio name")} value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
            <button className="button" disabled={create.isPending}>{tr("Add")}</button>
          </form>
          {err && <p className="bad">{err}</p>}
        </section>
      )}
    </>
  )
}
